import { useEffect } from 'react';
import Api from 'Vendor/Api';
import { Font } from 'Vendor/Utils/StudioTypes';

const fontCache = new Map<string, FontFace>();

/**
 * Hook to load a font variant by its name using platformFonts from props
 */
const useFontLoader = (fontName: string | null, platformFonts: Font[] = []) => {
  useEffect(() => {
    if (!fontName) return;

    // Find the font object by name in the passed platformFonts
    const font: Font | undefined = platformFonts.find(f => f.family === fontName);
    if (!font || !font.variants || font.variants.length === 0) return;

    // Load all variants
    font.variants.forEach(async (variant) => {
      if (!variant.file) return;

      if (fontCache.has(variant.file)) {
        document.fonts.add(fontCache.get(variant.file)!);
        return;
      }

      try {
        const res = await Api.get(`/cdn/font/file/${variant.file}`, { responseType: 'arraybuffer' });
        const blob = new Blob([res.data], { type: 'font/woff2' }); // adjust type if needed
        const fontUrl = URL.createObjectURL(blob);

        const fontFace = new FontFace(font.family, `url(${fontUrl})`, {
          weight: variant.weight?.toString(),
        });

        const loadedFont = await fontFace.load();
        document.fonts.add(loadedFont);
        fontCache.set(variant.file, loadedFont);
      } catch (err) {
        console.error(`Failed to load font variant: ${variant.file}`, err);
      }
    });
  }, [fontName, platformFonts]);
};

export default useFontLoader;
